import { MapViewState, WebMercatorViewport } from "@deck.gl/core";
import { MutableRefObject, useCallback, useEffect, useState } from "react";
import type { MapProps } from "react-map-gl/mapbox";
import { useOrientation } from "@uidotdev/usehooks";

import { MapWidgetConfigurationAllOfBaseLayerCoords } from "@/lib/api";
import { layoutSignal } from "@/store/layout-signal";

export interface UseMapViewStateProps {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  initialCoords: MapWidgetConfigurationAllOfBaseLayerCoords[];
  fitBoundsOptions?: Parameters<WebMercatorViewport["fitBounds"]>[1];
  /**
   * Fit again when the container size changes. The big map leaves this off so a
   * resize does not undo a pan. Table thumbs need it: a fluid column shrinks
   * after the first fit, and the old zoom then crops the country.
   */
  refitOnResize?: boolean;
}

type OnMoveFn = NonNullable<MapProps["onMove"]>;

export interface UseMapViewState {
  mapViewState: MapViewState | undefined;
  onMove: OnMoveFn;
}

const useMapViewState = ({
  containerRef,
  initialCoords,
  fitBoundsOptions,
  refitOnResize = false,
}: UseMapViewStateProps): UseMapViewState => {
  const [mapViewState, setMapViewState] = useState<MapViewState | undefined>(undefined);
  const [size, setSize] = useState(() => [
    containerRef.current?.offsetWidth ?? 0,
    containerRef.current?.offsetWidth ?? 0,
  ]);

  const recalculateSize = useCallback(() => {
    if (!containerRef.current) return;
    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    setSize([width, height]);
  }, [containerRef.current, setSize]);

  const orientation = useOrientation();
  useEffect(() => {
    const sub = layoutSignal.on("layoutChanged", () => {
      recalculateSize();
    });
    return () => sub.unsubscribe();
  }, [recalculateSize]);

  useEffect(() => {
    // Wait for timeout so container has had time to resize
    const timeout = setTimeout(() => {
      recalculateSize();
    }, 250);
    return () => {
      clearTimeout(timeout);
    };
  }, [containerRef.current, orientation]);

  // Measure as soon as the container gets a real size. Hosts that mount already
  // laid out (dashboard, sidebar preview) are caught by the timeout above, but a
  // container that starts at 0 and is sized later — e.g. the fullscreen preview
  // modal after it lays out — would otherwise never render until a window
  // resize. `initialViewState` is read once by the map, so a single valid
  // measurement is enough; disconnect afterwards to avoid refitting on every
  // later resize (the map handles its own canvas resizing from there).
  // Thumbs pass `refitOnResize` and stay subscribed, or a narrower column
  // keeps the first zoom and crops the country.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box && box.width > 0 && box.height > 1) {
        recalculateSize();
        if (!refitOnResize) observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [recalculateSize, refitOnResize]);

  useEffect(() => {
    const [width, height] = size;
    const hasValidSize = width > 0 && height > 1;
    if (!hasValidSize) {
      // A transient 0-size (e.g. the preview host briefly `display:none` while
      // the layout reflows on resize) must NOT tear down an already-mounted
      // map: unmounting `<MapGL>` re-creates the interleaved Deck overlay,
      // whose GeoJSON layers then fail to repaint on reveal. Keep the last
      // valid viewport; the map manages its own canvas resizing from there.
      return;
    }
    if (
      initialCoords &&
      initialCoords.length === 2 &&
      initialCoords.every((l) => l.lat !== undefined && l.lng !== undefined)
    ) {
      let mercViewport = new WebMercatorViewport({
        width,
        height,
        pitch: 0,
        bearing: 0,
        orthographic: false,
      });
      mercViewport = mercViewport.fitBounds(
        [
          [Number(initialCoords[0].lng), Number(initialCoords[0].lat)],
          [Number(initialCoords[1].lng), Number(initialCoords[1].lat)],
        ],
        fitBoundsOptions,
      );
      setMapViewState(mercViewport);
      window?.dispatchEvent(new Event("resize"));
    }
  }, [initialCoords, fitBoundsOptions, size]);

  /** Use with controlled Map `viewState` so fit-to-bounds updates apply (e.g. changing data subset). */
  const onMove: OnMoveFn = useCallback((event) => {
    setMapViewState(event.viewState as MapViewState);
  }, []);

  return { mapViewState, onMove };
};

export default useMapViewState;
